const DEFAULT_TEMPERATURE = 0.7;

const defaultFeatures = () => ({
  faqsFromResolved: false,
  memories: true,
  citations: false,
});

const parseTemperature = value => {
  if (value == null || value === '') return DEFAULT_TEMPERATURE;

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_TEMPERATURE;

  return Math.min(1, Math.max(0, parsed));
};

const fromHistory = entry => ({
  id: Number(entry.id || entry.created_at || Date.now()),
  createdAt: entry.createdAt || entry.created_at || new Date().toISOString(),
  userName: entry.userName || entry.user_name || '',
  content: entry.content || '',
  added: entry.added || [],
  removed: entry.removed || [],
  reordered: Boolean(entry.reordered),
});

const toHistory = entry => ({
  id: entry.id,
  created_at: entry.createdAt,
  user_name: entry.userName,
  content: entry.content,
  added: entry.added || [],
  removed: entry.removed || [],
  reordered: Boolean(entry.reordered),
});

const SCALAR_KEYS = {
  name: 'name',
  description: 'description',
  instructions: 'instructions',
  handoffMessage: 'handoff_message',
  resolutionMessage: 'resolution_message',
  autoResolveMode: 'auto_resolve_mode',
  inactivityThresholdMinutes: 'inactivity_threshold_minutes',
  sendInactivityResolutionMessage: 'send_inactivity_resolution_message',
  sendFollowUpMessage: 'send_follow_up_message',
  includeAttachmentUrls: 'include_attachment_urls',
  includeContactInfo: 'include_contact_info',
  followUpMessage: 'follow_up_message',
  followUpThresholdMinutes: 'follow_up_threshold_minutes',
  responseWindow: 'response_window',
  scheduleSource: 'schedule_source',
  timeZone: 'time_zone',
  temperature: 'temperature',
};

export const fromApi = raw => ({
  id: Number(raw.id),
  name: raw.name || '',
  description: raw.description || '',
  createdBy: raw.created_by || raw.createdBy || '',
  createdAt: raw.created_at || raw.createdAt || new Date().toISOString(),
  features: {
    ...defaultFeatures(),
    faqsFromResolved: Boolean(raw.features?.faqs_from_resolved),
    memories: Boolean(raw.features?.memories),
    citations: Boolean(raw.features?.citations),
  },
  handoffMessage: raw.handoff_message || '',
  resolutionMessage: raw.resolution_message || '',
  instructions: raw.instructions || '',
  autoResolveMode: raw.auto_resolve_mode || 'evaluated',
  inactivityThresholdMinutes: Number(raw.inactivity_threshold_minutes || 60),
  sendInactivityResolutionMessage:
    raw.send_inactivity_resolution_message !== false,
  sendFollowUpMessage: raw.send_follow_up_message === true,
  includeAttachmentUrls: raw.include_attachment_urls === true,
  includeContactInfo: raw.include_contact_info === true,
  followUpMessage: raw.follow_up_message || '',
  followUpThresholdMinutes: Number(raw.follow_up_threshold_minutes || 15),
  responseWindow: raw.response_window || 'always',
  scheduleSource: raw.schedule_source || 'channel',
  timeZone: raw.time_zone || '',
  temperature: parseTemperature(raw.temperature),
  inboxIds: Array.isArray(raw.inbox_ids) ? raw.inbox_ids : [],
  limits: Array.isArray(raw.limits) ? raw.limits : [],
  replyStyles: Array.isArray(raw.reply_styles) ? raw.reply_styles : [],
  promptHistory: Array.isArray(raw.prompt_history)
    ? raw.prompt_history.slice(0, 30).map(fromHistory)
    : [],
});

export const toApiPayload = payload => {
  const body = {};

  Object.entries(SCALAR_KEYS).forEach(([camel, snake]) => {
    if (payload[camel] !== undefined) body[snake] = payload[camel];
  });

  if (payload.features) {
    const features = {};
    if (payload.features.memories !== undefined) {
      features.memories = Boolean(payload.features.memories);
    }
    if (payload.features.faqsFromResolved !== undefined) {
      features.faqs_from_resolved = Boolean(payload.features.faqsFromResolved);
    }
    if (payload.features.citations !== undefined) {
      features.citations = Boolean(payload.features.citations);
    }
    body.features = features;
  }

  if (payload.limits) body.limits = payload.limits;
  if (payload.replyStyles) body.reply_styles = payload.replyStyles;
  if (payload.promptHistory) {
    body.prompt_history = payload.promptHistory.map(toHistory);
  }

  return body;
};
